import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { FormTemplateItem } from 'types/formTemplate';
import { fetchAddress, fetchFormByProperty } from './formTemplateThunks';
import { Address } from './formTemplateTypes';
import { secureLocalStorage } from '../../utils/secureStorage';

interface UploadedDocument {
    document_id: string;
    document_description: string;
    pet_type?: string;
    pet_index?: number | null;
    document_name?: string;
    original_filename?: string;
    file_size_bytes?: number;
    file_size?: string;
    mime_type?: string;
    property_id?: string;
    pet_id?: string;
    unique_key?: number | null;
}

interface FormTemplateState {
    templates: FormTemplateItem[];
    loading: boolean;
    error: string | null;
    uploadedDocuments: UploadedDocument[];
    inProgress?: boolean;
    addressSuggestions: Address[];
    addressPlaceId: string;
    loadedPetIds: string[]; // Track which pet IDs from stageOnePetsData have been loaded
}
const initialState: FormTemplateState = {
    templates: [],
    loading: false,
    error: null,
    uploadedDocuments: [],
    addressSuggestions: [],
    addressPlaceId: "",
    loadedPetIds: []
};

const formTemplateSlice = createSlice({
    name: 'formTemplate',
    initialState,
    reducers: {
        setTemplates(state, action: PayloadAction<FormTemplateItem[]>) {
            state.templates = action.payload;
        },
        setLoading(state, action: PayloadAction<boolean>) {
            state.loading = action.payload;
        },
        setError(state, action: PayloadAction<string | null>) {
            state.error = action.payload;
        },
        addUploadedDocument(state, action: PayloadAction<UploadedDocument>) {
            const idx = state.uploadedDocuments.findIndex(doc => doc.document_id === action.payload.document_id);
            if (idx !== -1) {
                state.uploadedDocuments[idx].document_description = action.payload.document_description;
            } else {
                state.uploadedDocuments.push(action.payload);
            }
        },
        removeUploadedDocument(state, action: PayloadAction<string>) {
            state.uploadedDocuments = state.uploadedDocuments.filter(doc => doc.document_id !== action.payload);
        },
        removeUploadDocByPetPos(state, action: PayloadAction<{animal_index: number | null, pet_type?: string}>) {
            const { animal_index, pet_type } = action.payload;
            // Remove documents that match BOTH the pet_index AND pet_type
            // Keep documents where either condition is false (different index OR different type)
            state.uploadedDocuments = state.uploadedDocuments.filter(doc => 
                !(doc.pet_index === animal_index && doc.pet_type === pet_type)
            );
        },
        /**
         * Reindexes uploaded documents for a specific pet type after a pet is removed.
         * Shifts pet_index values down by 1 for all pets after the removed position.
         * 
         * @param action.payload.pet_type - The type of pet (dog, cat, etc.)
         * @param action.payload.removedIndex - The 1-based index of the removed pet
         */
        reindexUploadedDocuments(state, action: PayloadAction<{pet_type: string, removedIndex: number}>) {
            const { pet_type, removedIndex } = action.payload;
            
            state.uploadedDocuments = state.uploadedDocuments.map(doc => {
                // Only reindex documents of the same pet type that are after the removed position
                if (doc.pet_type === pet_type && doc.pet_index && doc.pet_index > removedIndex) {
                    return {
                        ...doc,
                        pet_index: doc.pet_index - 1
                    };
                }
                return doc;
            });
        },
        setAgentInfo(state, action: PayloadAction<any>) {
            secureLocalStorage.setItem('agent_name', action.payload?.name || '');
            secureLocalStorage.setItem('agent_company', action.payload?.company || '');
        },
        /**
         * Mark a pet ID as having its documents loaded from stageOnePetsData
         * This prevents re-loading stale documents when the same position is reused
         */
        markPetDocumentsLoaded(state, action: PayloadAction<string>) {
            if (!state.loadedPetIds.includes(action.payload)) {
                state.loadedPetIds.push(action.payload);
            }
        },
        /**
         * Remove a pet ID from the loaded set when the pet is deleted
         * This allows documents to be loaded again if needed
         */
        unmarkPetDocumentsLoaded(state, action: PayloadAction<string>) {
            state.loadedPetIds = state.loadedPetIds.filter(id => id !== action.payload);
        },
        /**
         * Clear all loaded pet IDs (useful when refreshing tenant data)
         */
        clearLoadedPetIds(state) {
            state.loadedPetIds = [];
        },
        setAddressSuggestionList(state, action: PayloadAction<Address[]>) {
            state.addressSuggestions = action.payload;
        },
        clearAddressSuggestionList(state) {
            state.addressSuggestions = [];
        },
        setAddressPlaceId(state, action: PayloadAction<string>) {
            state.addressPlaceId = action.payload;
        },
        clearAddressPlaceId(state) {
            state.addressPlaceId = "";
        }
    },
    extraReducers: (builder) => {
        builder.addCase(fetchFormByProperty.pending, (state, action) => {
          state.inProgress = true;
          state.error = null;
        });
        builder.addCase(fetchFormByProperty.fulfilled, (state, action) => {
          state.inProgress = false;
        });
        builder.addCase(fetchFormByProperty.rejected, (state, action) => {
          state.inProgress = false;
          state.error = typeof action.payload === "string" ? action.payload : "Unknown error";
        });

        builder.addCase(fetchAddress.pending, (state, action) => {
          state.inProgress = true;
          state.error = null;
        });
        builder.addCase(fetchAddress.fulfilled, (state, action) => {
          state.inProgress = false;
        });
        builder.addCase(fetchAddress.rejected, (state, action) => {
          state.inProgress = false;
          state.error = typeof action.payload === "string" ? action.payload : "Unknown error";
        });
    }
});

export const { 
    setTemplates, 
    setLoading, 
    setError, 
    setAddressSuggestionList,
    clearAddressSuggestionList, 
    setAddressPlaceId, 
    clearAddressPlaceId, 
    addUploadedDocument, 
    removeUploadDocByPetPos, 
    reindexUploadedDocuments, 
    removeUploadedDocument, 
    setAgentInfo,
    markPetDocumentsLoaded,
    unmarkPetDocumentsLoaded,
    clearLoadedPetIds
} = formTemplateSlice.actions;
export default formTemplateSlice.reducer;
